/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Environments and deployments", description = "Hightouch supports change management in-app via separate workspaces for different environments, for example, production, staging, development, and sandbox.", lastUpdatedDate = new Date(1790553600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Environments and deployments",
        description: "Hightouch supports change management in-app via separate workspaces for different environments, for example, production, staging, development, and sandbox.",
        lastUpdatedDate: "Mon Sep 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How do environments work?",
          "depth": 0,
          "id": "how-do-environments-work"
        }, {
          "value": "Separate resources for separate environments",
          "depth": 1,
          "id": "separate-resources-for-separate-environments"
        }, {
          "value": "Resource dependencies",
          "depth": 1,
          "id": "resource-dependencies"
        }, {
          "value": "Environment usage",
          "depth": 0,
          "id": "environment-usage"
        }, {
          "value": "Link resources",
          "depth": 1,
          "id": "link-resources"
        }, {
          "value": "Deploy changes",
          "depth": 1,
          "id": "deploy-changes"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "What permissions do you need to use environments?",
          "depth": 1,
          "id": "what-permissions-do-you-need-to-use-environments"
        }, {
          "value": "Can you link any resource to another resource of the same type?",
          "depth": 1,
          "id": "can-you-link-any-resource-to-another-resource-of-the-same-type"
        }, {
          "value": "How do you link existing models and syncs?",
          "depth": 1,
          "id": "how-do-you-link-existing-models-and-syncs"
        }, {
          "value": "Can you use environments to copy resources?",
          "depth": 1,
          "id": "can-you-use-environments-to-copy-resources"
        }, {
          "value": "Can you only deploy changes to production environments?",
          "depth": 1,
          "id": "can-you-only-deploy-changes-to-production-environments"
        }, {
          "value": "Can you use environments and deployments with Git Sync?",
          "depth": 1,
          "id": "can-you-use-environments-and-deployments-with-git-sync"
        }, {
          "value": "Can you use environments and deployments with Hightouch Event Collection?",
          "depth": 1,
          "id": "can-you-use-environments-and-deployments-with-hightouch-event-collection"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      strong: "strong",
      a: "a",
      img: "img",
      h2: "h2",
      h3: "h3",
      em: "em",
      code: "code",
      h4: "h4",
      ol: "ol",
      li: "li"
    }, _provideComponents(), props.components), {BtOnly, PylonLink} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Environments and deployments",
        plural: true
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Not sure whether you need environments or a separate workspace?"
          }), "\nEnvironments are the right choice when your teams share the same sources,\ndestinations, and user roster but need a safe promotion path (dev → staging →\nprod). If you need hard isolation — separate sources, separate users, nothing\nshared — you need a separate workspace instead. For a side-by-side comparison,\nsee ", _jsx(_components.a, {
            href: "/workspace-management/workspaces#do-you-need-another-workspace",
            children: "Do you need another\nworkspace?"
          })]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Maintaining separate ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/Deployment_environment",
          children: "environments"
        }), " for different stages of the release process is a common practice across technical domains."]
      }), "\n", _jsx(_components.p, {
        children: "Separating environments minimizes the risk of accidental changes or disruptions to the user-facing experience.\nFor example, environments can help you avoid overwriting CRM data you didn't mean to, activating a marketing campaign prematurely, or incurring unexpected data warehouse charges."
      }), "\n", _jsx(_components.p, {
        children: "Generally, production environments are for live, user-facing applications, staging for pre-release testing, and development for experimentation."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/environments/environments-diagram.png",
          alt: "Environments diagram"
        })
      }), "\n", _jsx(_components.p, {
        children: "You can set up whatever environments your organization requires in Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "how-do-environments-work",
        children: "How do environments work?"
      }), "\n", _jsxs(_components.p, {
        children: ["Each Hightouch ", _jsx(_components.a, {
          href: "/workspace-management/overview#workspaces",
          children: "workspace"
        }), " in your organization acts as a separate environment.\nYou can link resources between workspaces, which allows you to ", _jsx(_components.strong, {
          children: "deploy"
        }), " ", _jsx(_components.a, {
          href: "/getting-started/concepts",
          children: "models and syncs"
        }), " from one environment to another."]
      }), "\n", _jsxs(_components.p, {
        children: ["Each workspace can have its own set of ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: "alerts"
        }), " and can use its own extensions.\nEnvironments integrate seamlessly with other governance features, such as ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "role-based access controls"
        }), " (RBAC) and ", _jsx(_components.a, {
          href: "/workspace-management/approval-flows",
          children: "approval flows"
        }), ".\nWorkspace ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "permissions"
        }), " and ", _jsx(_components.a, {
          href: "/workspace-management/approval-flows",
          children: "drafts"
        }), " are enforced across workspaces when deploying changes."]
      }), "\n", _jsx(_components.p, {
        children: "You can set up your organization so that specific team members have access to certain resources.\nFor example, some team members, such as your data team, may have permission to set up sources and models.\nOthers, such as the marketing team, can deploy or draft sync deployments."
      }), "\n", _jsx(_components.h3, {
        id: "separate-resources-for-separate-environments",
        children: "Separate resources for separate environments"
      }), "\n", _jsxs(_components.p, {
        children: ["Each workspace has its own set of resources, including ", _jsx(_components.a, {
          href: "/getting-started/concepts#sources",
          children: "sources"
        }), ", ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), ", ", _jsx(_components.a, {
          href: "/getting-started/concepts#destinations",
          children: "destinations"
        }), ", and ", _jsx(_components.a, {
          href: "/getting-started/concepts#syncs",
          children: "syncs"
        }), ".\nHightouch allows you to link resources across workspaces.\nFor example, you can link a Snowflake playground in your \"dev\" workspace to a production Snowflake instance in your \"prod\" workspace,\na Salesforce sandbox to your production Salesforce instance, etc."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/environments/linked-resources.png",
          alt: "Environments diagram"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Environments allow you to ", _jsx(_components.strong, {
          children: "deploy"
        }), " changes to models and syncs.\nWhen you deploy a model or sync, Hightouch copies its configuration to another workspace, updating or creating resources where necessary.\nBefore performing the copy, Hightouch checks that all ", _jsx(_components.a, {
          href: "#resource-dependencies",
          children: "underlying resources"
        }), " are appropriately configured."]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you may be experimenting with changes to a Salesforce sync ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "mapping"
        }), " in your development environment.\nWhen you're happy with your change, you can deploy it to your production environment.\nBefore copying your changes to a production sync, Hightouch checks that the underlying model and destination have linked counterparts in production."]
      }), "\n", _jsx(_components.p, {
        children: "Keeping resources separate between environments lets you create as many as you want in a development environment without cluttering other environments you may want to keep pristine.\nAnd since changes don't automatically deploy to linked resources,\nyou can safely delete or update resources without worrying about knock-on effects in other environments.\nEach resource also has its own history, which you can inspect, for example, by examining its activity."
      }), "\n", _jsx(_components.h3, {
        id: "resource-dependencies",
        children: "Resource dependencies"
      }), "\n", _jsxs(_components.p, {
        children: ["While any resources of the same type can be linked, for example, source to source and sync to sync, ", _jsx(_components.strong, {
          children: "you can only deploy models and syncs"
        }), ".\nTo deploy models and syncs, you must link their underlying sources and destinations to their relevant counterparts."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Environments apply to ", _jsx(_components.strong, {
            children: "Reverse ETL models and syncs only"
          }), ". Customer Studio\nresources — including audiences, traits, parent models, and schema models —\n", _jsx(_components.strong, {
            children: "cannot be deployed between environments"
          }), ". Each environment must be\nconfigured and maintained independently for Customer Studio workflows. For\nCustomer Studio change management, use ", _jsx(_components.a, {
            href: "/workspace-management/approval-flows",
            children: "Approval\nFlows"
          }), " instead."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When you ", _jsx(_components.a, {
          href: "#deploy-changes",
          children: "deploy"
        }), " models or syncs, Hightouch checks that the underlying resources match linked counterparts in the target environment.\nFor example, if you link one ", _jsx(_components.strong, {
          children: "source"
        }), " to another, you can deploy ", _jsx(_components.strong, {
          children: "models"
        }), " built off the linked source."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/environments/deploy-model.png",
          alt: "Deploying a model",
          title: "Model deployments check that linked sources of the same type exist in both workspaces."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/environments/deployed-model.png",
          alt: "Deployed model",
          title: "Once deployment validation passes, the deployment creates or updates a model in the target workspace."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To deploy ", _jsx(_components.strong, {
          children: "syncs"
        }), ", the underlying linked ", _jsx(_components.strong, {
          children: "models"
        }), " ", _jsx(_components.em, {
          children: "and"
        }), " ", _jsx(_components.strong, {
          children: "destinations"
        }), " need to match in both workspaces."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/environments/deploy-sync.png",
          alt: "Deploying a sync",
          title: "Sync deployments check that linked destinations and models exist in both workspaces."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/environments/deployed-sync.png",
          alt: "Deployed sync",
          title: "Once deployment validation passes, the deployment creates or updates a sync in the target workspace."
        })
      }), "\n", _jsx(_components.h2, {
        id: "environment-usage",
        children: "Environment usage"
      }), "\n", _jsxs(_components.p, {
        children: ["To deploy changes, you first need to link resources.\nYou can either link existing sources and destinations on their configuration pages,\nor link them as part of the ", _jsx(_components.a, {
          href: "#deploy-changes",
          children: "deployment workflow"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You only need to link underlying resources once to allow deployments.\nRemoving links between resources doesn't impact syncs or models you previously deployed."
      }), "\n", _jsx(_components.h3, {
        id: "link-resources",
        children: "Link resources"
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["To link resources, you need ", _jsxs(_components.a, {
            href: "/workspace-management/roles#custom-role-grant-reference",
            children: [_jsx(_components.code, {
              children: "create"
            }), "\npermissions"]
          }), " for the\nrelevant resource type (source or destination) in all workspaces you want to\ncreate the link between. For example, if you want to link two sources, you\nneed ", _jsxs(_components.a, {
            href: "/workspace-management/roles#custom-role-grant-reference",
            children: [_jsx(_components.code, {
              children: "create"
            }), "\npermissions"]
          }), " for\nsources in both workspaces you want to link them in."]
        })
      }), "\n", _jsx(_components.p, {
        children: "You can link existing sources and destinations to their counterparts in other workspaces.\nYou can only link models and syncs during the deployment workflow."
      }), "\n", _jsxs(_components.p, {
        children: ["When you ", _jsx(_components.a, {
          href: "#deploy-changes",
          children: "deploy"
        }), " a resource for the first time, you create and simultaneously link the resource to a counterpart in the target workspace.\nDuring deployments, Hightouch ensures you've linked the necessary ", _jsx(_components.a, {
          href: "#resource-dependencies",
          children: "underlying resources"
        }), ".\nIf you haven't, you can link them directly from the deployment wizard."]
      }), "\n", _jsx(_components.h4, {
        id: "link-sources-and-destinations",
        children: "Link sources and destinations"
      }), "\n", _jsx(_components.p, {
        children: "To link existing sources and destinations, follow these steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the resource's overview page and open the ", _jsx(_components.strong, {
            children: "Linking"
          }), " tab."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/environments/link-resources.png",
          alt: "Linking resources in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the relevant resource for each workspace you want to link and then click ", _jsx(_components.strong, {
            children: "Link"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch only displays eligible resources for linking. To be eligible: - the\nresources must be of the same type; for example, they both must be Snowflake -\nthe user creating the link must be a member of both workspaces - both\nworkspaces must belong to the same organization"
        })
      }), "\n", _jsx(_components.h4, {
        id: "link-syncs-and-models",
        children: "Link syncs and models"
      }), "\n", _jsxs(_components.p, {
        children: ["The only way to link syncs to other syncs and models to other models is by ", _jsx(_components.a, {
          href: "#deploy-changes",
          children: "deploying them"
        }), ".\nOnce deployment creates a new sync or model, it's automatically linked for future deployments."]
      }), "\n", _jsxs(_components.p, {
        children: ["You cannot deploy to existing syncs or models. If you want to link existing syncs or models from one workspace to another,\ndelete the development or testing version and recreate it by deploying it from your production environment.\nDon't hesitate to ", _jsx(PylonLink, {
          pylonText: "Hello, I have a question about environments and deployments",
          linkText: "get in touch"
        }), " if you have any questions or concerns."]
      }), "\n", _jsx(_components.h3, {
        id: "deploy-changes",
        children: "Deploy changes"
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["To deploy changes, you need ", _jsx(_components.a, {
            href: "/workspace-management/roles#approval-flows",
            children: "permission to at least create\ndrafts"
          }), " for the relevant resource\ntype (model or sync) in the target workspace to which you want to deploy\nchanges. For example, if you want to deploy a sync, you need permission to\ncreate sync drafts in the workspace you want to deploy the sync to."]
        })
      }), "\n", _jsx(_components.p, {
        children: "To deploy syncs or models, follow these instructions:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["From a model or sync's configuration page, click the deploy icon to the left of the ", _jsx(_components.strong, {
            children: "Run"
          }), " button and select ", _jsx(_components.strong, {
            children: "Deploy"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/environments/deploy-button.png",
          alt: "Deploying changes in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsx(_components.li, {
          children: "Select the environment you want to deploy the change to. The first time you deploy a particular resource, Hightouch creates a new resource in the target environment. If you've deployed the resource before, the deployment updates the previously created and linked resource."
        }), "\n", _jsxs(_components.li, {
          children: ["If the relevant ", _jsx(_components.a, {
            href: "#resource-dependencies",
            children: "underlying resources"
          }), " aren't already linked, follow the steps in the modal to link them."]
        }), "\n", _jsx(_components.li, {
          children: "The deployment wizard shows what has changed: added, removed, and updated configurations. Review these to ensure they're the changes you want to deploy."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/environments/review-deployment.png",
          alt: "Deploying changes in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "5",
        children: ["\n", _jsxs(_components.li, {
          children: ["For sync deployments, you can choose whether to deploy its ", _jsx(_components.a, {
            href: "/syncs/schedule-sync-ui#schedule-types",
            children: "schedule"
          }), " as well as the other configuration changes."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Deploy"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Syncs created via deployment are disabled by default."
          }), " If you want to enable a deployed sync, make sure to do so from its ", _jsx(_components.a, {
            href: "/syncs/overview#individual-sync-overview-page",
            children: "overview page"
          }), " once you've deployed it."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You can't deploy\n", _jsx(_components.a, {
            href: "/workspace-management/approval-flows#edit-existing-resources",
            children: "drafted"
          }), "\nchanges on a configuration. You first need to get approval and/or publish\nchanges to a resource before deploying the changes."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "deployment-validations",
        children: "Deployment validations"
      }), "\n", _jsxs(_components.p, {
        children: ["Before deploying changes, Hightouch validates dependencies.\nHightouch blocks deployments until you've resolved all dependency issues.\nFor example, if you try to deploy a ", _jsx(_components.a, {
          href: "/extensions/dbt-models",
          children: "dbt-based"
        }), " model from your staging to production workspace,\nbut the ", _jsx(_components.a, {
          href: "/extensions/dbt-models",
          children: "dbt extension"
        }), " isn't set up in production, Hightouch blocks the deployment.\nOnce you've resolved the issue, for example, by configuring a missing extension, you can retry the deployment."]
      }), "\n", _jsxs(_components.p, {
        children: ["Deployments will clear or replace any existing ", _jsx(_components.a, {
          href: "/workspace-management/approval-flows",
          children: "drafts or approval requests"
        }), " on a sync or model in a target workspace. You will be warned if a deployment will replace a draft during the validation step."]
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "what-permissions-do-you-need-to-use-environments",
        children: "What permissions do you need to use environments?"
      }), "\n", _jsxs(_components.p, {
        children: ["To link resources, you need ", _jsxs(_components.a, {
          href: "/workspace-management/roles#custom-role-grant-reference",
          children: [_jsx(_components.code, {
            children: "create"
          }), " permissions"]
        }), " for the relevant resource type in both workspaces you want to create the link between.\nTo deploy changes, you need ", _jsx(_components.a, {
          href: "/workspace-management/roles#approval-flows",
          children: "permission to create or update drafts"
        }), " for the relevant resource type in the target workspace you want to deploy changes to."]
      }), "\n", _jsx(_components.h3, {
        id: "can-you-link-any-resource-to-another-resource-of-the-same-type",
        children: "Can you link any resource to another resource of the same type?"
      }), "\n", _jsx(_components.p, {
        children: "You can link sources to other sources, models to other models, destinations to other destinations, and syncs to other syncs."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Customer Studio resources — including audiences, traits, parent models, and\nschema models — cannot be linked or deployed between environments."
          }), " Each\nenvironment must be set up and maintained independently for Customer Studio.\nFor Customer Studio change management, use ", _jsx(_components.a, {
            href: "/workspace-management/approval-flows",
            children: "Approval\nFlows"
          }), " instead."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Please ", _jsx(PylonLink, {
          pylonText: "Hello, I have a feature request. I'd like environments to support Customer Studio deployments.",
          linkText: "let us know"
        }), " if this is something you're interested in."]
      }), "\n", _jsx(_components.h3, {
        id: "how-do-you-link-existing-models-and-syncs",
        children: "How do you link existing models and syncs?"
      }), "\n", _jsxs(_components.p, {
        children: ["The only way to link syncs to other syncs and models to other models is by ", _jsx(_components.a, {
          href: "#deploy-changes",
          children: "deploying them"
        }), ".\nOnce deployment creates a new sync or model, it's automatically linked for future deployments."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to link existing syncs or models from one workspace to another,\ndelete the development or testing version and recreate it by deploying it from your production environment.\nDon't hesitate to ", _jsx(PylonLink, {
          pylonText: "Hello, I have a question about environments and deployments",
          linkText: "get in touch"
        }), " if you have any questions or concerns."]
      }), "\n", _jsx(_components.h3, {
        id: "can-you-use-environments-to-copy-resources",
        children: "Can you use environments to copy resources?"
      }), "\n", _jsxs(_components.p, {
        children: ["You can use ", _jsx(_components.a, {
          href: "#deploy-changes",
          children: "deployments"
        }), " to copy existing models and syncs but not sources and destinations.\nTo copy an existing model or sync to a different workspace, follow the instructions for ", _jsx(_components.a, {
          href: "#deploy-changes",
          children: "deploying changes"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "can-you-only-deploy-changes-to-production-environments",
        children: "Can you only deploy changes to production environments?"
      }), "\n", _jsxs(_components.p, {
        children: ["Deploying changes has no directionality.\nYou can deploy changes from a testing workspace to a production workspace and vice-versa, as long as the ", _jsx(_components.a, {
          href: "#deployment-validations",
          children: "validations"
        }), " pass."]
      }), "\n", _jsx(_components.h3, {
        id: "can-you-use-environments-and-deployments-with-git-sync",
        children: "Can you use environments and deployments with Git Sync?"
      }), "\n", _jsxs(_components.p, {
        children: ["If you already use ", _jsx(_components.a, {
          href: "/extensions/git-sync",
          children: "Git Sync"
        }), " to manage your syncs and destinations across workspaces, deploying a sync or model to your target workspace would create its corresponding ", _jsx(_components.code, {
          children: "yaml"
        }), " file in the configured Git Sync repository.\nBeyond updating ", _jsx(_components.code, {
          children: "yaml"
        }), " files, environments and deployments don't interact with your Git Sync repositories."]
      }), "\n", _jsxs(_components.p, {
        children: ["You should use ", _jsx(_components.em, {
          children: "either"
        }), " Git Sync ", _jsx(_components.em, {
          children: "or"
        }), " the in-app environments and deployments feature for your environment management."]
      }), "\n", _jsx(_components.h3, {
        id: "can-you-use-environments-and-deployments-with-hightouch-event-collection",
        children: "Can you use environments and deployments with Hightouch Event Collection?"
      }), "\n", _jsx(_components.p, {
        children: "At the moment, it's not possible to create deployments of event resources like event syncs, event sources or event destinations and so it isn't currently possible to separate your Event Collection resources into staging and production environments."
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
